import React, { useEffect, useState } from 'react';
import { useForm, useFieldArray } from 'react-hook-form';
import { cleanText, isCleanText } from '@/utils/sanitize';
import { Plus, Trash2, Save, X } from 'lucide-react';
import Modal from '../ui/Modal';
import { Input } from '../ui/Input';
import { Select } from '../ui/Select';
import { Toggle } from '../ui/Toggle';
import { DifficultyLevel } from './DifficultyBadge';
import { QuestionType } from './QuestionCard';

interface QuestionOption {
  id: string;
  text: string;
  is_correct: boolean;
}

interface QuestionFormData {
  question_text: string;
  question_type: QuestionType;
  options?: QuestionOption[];
  correct_answer?: string;
  answer_keywords?: string;
  case_sensitive?: boolean;
  points: number;
  difficulty_level: DifficultyLevel;
  bloom_level?: string;
  topic?: string;
  explanation: string;
  hint?: string;
}

interface ExamQuestion extends QuestionFormData {
  id: string;
  course_id?: string;
  source: 'AI_GENERATED' | 'MANUAL' | 'IMPORTED';
  order: number;
}

interface QuestionEditorModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (question: Partial<ExamQuestion>) => void;
  question?: ExamQuestion | null;
  mode: 'create' | 'edit';
}

const questionTypeOptions = [
  { label: 'Single Choice', value: 'SINGLE_CHOICE' },
  { label: 'Multiple Choice', value: 'MULTIPLE_CHOICE' },
  { label: 'True/False', value: 'TRUE_FALSE' },
  { label: 'Short Answer', value: 'SHORT_ANSWER' },
  { label: 'Long Answer', value: 'LONG_ANSWER' },
];

const difficultyOptions = [
  { label: 'Easy', value: 'EASY' },
  { label: 'Medium', value: 'MEDIUM' },
  { label: 'Hard', value: 'HARD' },
];

const bloomLevelOptions = [
  { label: 'Remember', value: 'REMEMBER' },
  { label: 'Understand', value: 'UNDERSTAND' },
  { label: 'Apply', value: 'APPLY' },
  { label: 'Analyze', value: 'ANALYZE' },
  { label: 'Evaluate', value: 'EVALUATE' },
  { label: 'Create', value: 'CREATE' },
];

export const QuestionEditorModal: React.FC<QuestionEditorModalProps> = ({
  isOpen,
  onClose,
  onSave,
  question,
  mode,
}) => {
  const {
    control,
    handleSubmit,
    watch,
    setValue,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<QuestionFormData>({
    defaultValues: {
      question_text: '',
      question_type: 'SINGLE_CHOICE',
      options: [
        { id: '1', text: '', is_correct: true },
        { id: '2', text: '', is_correct: false },
      ],
      points: 1,
      difficulty_level: 'MEDIUM',
      explanation: '',
    },
  });

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'options',
  });

  const questionType = watch('question_type');

  // Reset form when question changes
  useEffect(() => {
    if (question && mode === 'edit') {
      reset({
        question_text: question.question_text,
        question_type: question.question_type,
        options: question.options || [],
        correct_answer: question.correct_answer,
        answer_keywords: question.answer_keywords?.join(', '),
        case_sensitive: question.case_sensitive,
        points: question.points,
        difficulty_level: question.difficulty_level,
        bloom_level: question.bloom_level,
        topic: question.topic,
        explanation: question.explanation,
        hint: question.hint,
      });
    } else if (mode === 'create') {
      reset({
        question_text: '',
        question_type: 'SINGLE_CHOICE',
        options: [
          { id: '1', text: '', is_correct: true },
          { id: '2', text: '', is_correct: false },
        ],
        points: 1,
        difficulty_level: 'MEDIUM',
        explanation: '',
      });
    }
  }, [question, mode, reset, isOpen]);

  const onSubmit = (data: QuestionFormData) => {
    const formattedData: Partial<ExamQuestion> = {
      ...data,
      answer_keywords: data.answer_keywords
        ? data.answer_keywords.split(',').map((k) => k.trim())
        : undefined,
    };

    // Sanitize free-text before save (VAPT May 2026, #12/#13)
    formattedData.question_text = cleanText(data.question_text);
    if (formattedData.correct_answer) formattedData.correct_answer = cleanText(data.correct_answer);
    if (formattedData.options) {
      formattedData.options = formattedData.options.map((o: any) => ({
        ...o,
        text: cleanText(o.text),
        explanation: o.explanation ? cleanText(o.explanation) : o.explanation,
      }));
    }
    formattedData.explanation = cleanText(data.explanation);
    if (formattedData.hint) formattedData.hint = cleanText(formattedData.hint);
    if (formattedData.answer_keywords) {
      formattedData.answer_keywords = formattedData.answer_keywords
        .map((k: string) => cleanText(k))
        .filter((k: string) => k.length > 0);
    }

    if (mode === 'edit' && question) {
      formattedData.id = question.id;
    }

    // Mark as MANUAL if edited
    if (mode === 'edit' && question?.source === 'AI_GENERATED') {
      formattedData.source = 'MANUAL';
    } else if (mode === 'create') {
      formattedData.source = 'MANUAL';
    }

    onSave(formattedData);
    onClose();
  };

  const handleAddOption = () => {
    append({
      id: Date.now().toString(),
      text: '',
      is_correct: false,
    });
  };

  const handleRemoveOption = (index: number) => {
    if (fields.length > 2) {
      remove(index);
    }
  };

  const needsOptions =
    questionType === 'SINGLE_CHOICE' ||
    questionType === 'MULTIPLE_CHOICE' ||
    questionType === 'TRUE_FALSE';

  const needsTextAnswer =
    questionType === 'SHORT_ANSWER' || questionType === 'LONG_ANSWER';

  const footer = (
    <div className="flex items-center justify-end gap-3">
      <button
        type="button"
        onClick={onClose}
        className="
          px-4 py-2 text-sm font-medium
          text-gray-700 bg-white border border-gray-300 rounded-lg
          hover:bg-gray-50 transition-colors
          focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2
        "
      >
        Cancel
      </button>

      <button
        type="submit"
        form="question-form"
        disabled={isSubmitting}
        className="
          flex items-center gap-2 px-4 py-2 text-sm font-medium
          text-white bg-blue-600 rounded-lg
          hover:bg-blue-700 disabled:bg-blue-400 transition-colors
          focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2
        "
      >
        {isSubmitting ? (
          <>
            <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
            <span>Saving...</span>
          </>
        ) : (
          <>
            <Save className="w-4 h-4" />
            <span>{mode === 'create' ? 'Add Question' : 'Save Changes'}</span>
          </>
        )}
      </button>
    </div>
  );

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={mode === 'create' ? 'Add New Question' : 'Edit Question'}
      size="xl"
      footer={footer}
      closeOnOverlayClick={false}
    >
      <form id="question-form" onSubmit={handleSubmit(onSubmit)} className="space-y-6">
        {/* Question Text */}
        <Input
          name="question_text"
          control={control}
          label="Question Text"
          placeholder="Enter the question..."
          required
          error={errors.question_text?.message}
          rules={{
            required: 'Question text is required',
            minLength: { value: 10, message: 'Question must be at least 10 characters' },
            validate: (v: string) =>
              isCleanText(v) || 'Please use only letters, numbers and standard punctuation',
          }}
        />

        {/* Question Type & Difficulty Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <Select
            name="question_type"
            control={control}
            label="Question Type"
            options={questionTypeOptions}
            required
          />

          <Select
            name="difficulty_level"
            control={control}
            label="Difficulty Level"
            options={difficultyOptions}
            required
          />
        </div>

        {/* Options Section (for MCQ, SCQ, T/F) */}
        {needsOptions && (
          <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
            <div className="flex items-center justify-between mb-3">
              <label className="text-sm font-medium text-gray-700">Answer Options</label>
              {questionType !== 'TRUE_FALSE' && (
                <button
                  type="button"
                  onClick={handleAddOption}
                  className="
                    flex items-center gap-1 px-3 py-1.5 text-xs font-medium
                    text-blue-700 bg-blue-100 rounded-lg
                    hover:bg-blue-200 transition-colors
                  "
                >
                  <Plus className="w-3.5 h-3.5" />
                  Add Option
                </button>
              )}
            </div>

            <div className="space-y-3">
              {fields.map((field, index) => (
                <div key={field.id} className="flex items-start gap-3">
                  <div className="flex-1">
                    <Input
                      name={`options.${index}.text` as any}
                      control={control}
                      placeholder={`Option ${String.fromCharCode(65 + index)}`}
                      required
                      error={errors.options?.[index]?.text?.message}
                      rules={{ required: 'Option text is required' }}
                    />
                  </div>

                  <div className="flex items-center gap-2 pt-2">
                    <Toggle
                      name={`options.${index}.is_correct` as any}
                      control={control}
                      label=""
                      helperText="Correct"
                    />

                    {fields.length > 2 && questionType !== 'TRUE_FALSE' && (
                      <button
                        type="button"
                        onClick={() => handleRemoveOption(index)}
                        className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Text Answer Section (for Short/Long Answer) */}
        {needsTextAnswer && (
          <div className="space-y-4 p-4 bg-gray-50 rounded-lg border border-gray-200">
            <Input
              name="correct_answer"
              control={control}
              label="Expected Answer"
              placeholder="Enter the expected answer..."
              helperText="Provide a model answer for reference"
            />

            <Input
              name="answer_keywords"
              control={control}
              label="Answer Keywords"
              placeholder="keyword1, keyword2, keyword3"
              helperText="Comma-separated keywords for evaluation"
            />

            <Toggle
              name="case_sensitive"
              control={control}
              label="Case Sensitive"
              helperText="Enable case-sensitive keyword matching"
            />
          </div>
        )}

        {/* Additional Settings Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <Input
            name="points"
            control={control}
            label="Points"
            type="number"
            placeholder="1"
            required
            error={errors.points?.message}
            rules={{
              required: 'Points are required',
              min: { value: 1, message: 'Minimum 1 point' },
            }}
          />

          <Select
            name="bloom_level"
            control={control}
            label="Bloom's Level"
            options={bloomLevelOptions}
            placeholder="Select level..."
          />

          <Input
            name="topic"
            control={control}
            label="Topic/Tag"
            placeholder="e.g., Variables"
            helperText="Optional categorization"
          />
        </div>

        {/* Explanation */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Explanation <span className="text-red-500">*</span>
          </label>
          <textarea
            {...control.register('explanation', {
              required: 'Explanation is required',
            })}
            rows={3}
            className="
              w-full px-3 py-2 border border-gray-300 rounded-lg
              focus:ring-2 focus:ring-blue-500 focus:border-blue-500
              text-sm resize-none
            "
            placeholder="Explain why the answer is correct..."
          />
          {errors.explanation && (
            <p className="text-red-500 text-xs mt-1">{errors.explanation.message}</p>
          )}
        </div>

        {/* Hint */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Hint (Optional)</label>
          <textarea
            {...control.register('hint')}
            rows={2}
            className="
              w-full px-3 py-2 border border-gray-300 rounded-lg
              focus:ring-2 focus:ring-blue-500 focus:border-blue-500
              text-sm resize-none
            "
            placeholder="Provide a helpful hint..."
          />
        </div>
      </form>
    </Modal>
  );
};
